/**
 * Registre des modules (D21, ARTO-56), paliers et quotas (architecture, 6.4).
 *
 * LA SOURCE DE VÉRITÉ du découpage est la page commerciale « Composez votre
 * Artosera » (`15-modules-artosera.html`, tableaux `core`, `modules` et
 * `services`) : ce registre en reprend les identifiants tels quels.
 *
 * UN SEUL REGISTRE, et une seule fonction pour en tirer la liste des modules
 * d'une galerie — `modulesDeLaGalerie` —, appelée à l'identique par l'API, le
 * worker, la console et l'assemblage du site. La régie ne recalcule rien :
 * elle lit la liste effective que `GET /v1/auth/me` lui rend.
 *
 * Quatre familles :
 *
 *   - le CŒUR, toujours actif, jamais désactivable — ni par galerie, ni par
 *     l'interrupteur global ;
 *   - les modules COMPLÉMENTAIRES, vendus en option, DÉSACTIVÉS PAR DÉFAUT ;
 *   - les modules liés à un SERVICE (le site, la boutique) : désactivés par
 *     défaut, activés par la console quand le service est vendu ;
 *   - les ADD-ONS : un droit daté et un signal de publication, sans palier.
 *
 * Une galerie n'a, hors du cœur, que les modules de sa liste EXPLICITE
 * `modulesActifs`, posée par la console. Il n'y a plus de préréglage de
 * palier : le palier reste une donnée commerciale (et ses quotas), sans effet
 * sur les modules. Désactiver un module ne supprime jamais une donnée ; le
 * réactiver restitue tout.
 *
 * À ne pas confondre avec le palier COMMERCIAL d'une galerie (`PALIERS`) : le
 * PALIER DE DÉBLOCAGE d'un module (`PALIERS_MODULE` : MVP, V1, V2, finale) dit
 * quel lot de développement, financé par les associés, l'apporte. Un palier
 * se débloque pour TOUT le parc, module par module, depuis la console ; un
 * module non débloqué est fermé comme s'il était coupé globalement
 * (`docs/dev/modules.md`). Le cœur est au MVP, toujours débloqué.
 *
 * Chaque module déclare ce qu'il COUVRE — préfixes de routes API de galerie,
 * entrées de navigation de la régie, types de travaux du worker, blocs du site
 * public, scopes de jetons d'agent. Un test d'exhaustivité de l'API parcourt
 * les routeurs de galerie montés et les travaux enregistrés, et échoue sur
 * tout ce qui n'est rattaché à aucun module (`docs/dev/modules.md`).
 */
import { z } from 'zod';
// ── Paliers ──────────────────────────────────────────────────────────────────
/** Les paliers commerciaux : une donnée de la fiche, sans effet sur les modules. */
export const PALIERS = ['essentiel', 'galerie', 'groupe'];
export const palierSchema = z.enum(PALIERS);
// ── Paliers de déblocage ─────────────────────────────────────────────────────
/**
 * Les paliers de DÉBLOCAGE des modules, dans l'ordre du financement : chaque
 * palier payé par les associés débloque son lot pour toute la plateforme. Le
 * MVP porte le cœur et ne se verrouille jamais.
 */
export const PALIERS_MODULE = ['mvp', 'v1', 'v2', 'finale'];
export const palierModuleSchema = z.enum(PALIERS_MODULE);
/** Clés i18n des libellés de paliers (`commun:paliersModule.<id>`), FR et EN. */
export const LIBELLES_PALIERS_MODULE = {
    mvp: 'commun:paliersModule.mvp',
    v1: 'commun:paliersModule.v1',
    v2: 'commun:paliersModule.v2',
    finale: 'commun:paliersModule.finale',
};
/** Un palier dont les modules peuvent être verrouillés : tout sauf le MVP (le cœur). */
export function palierVerrouillable(palier) {
    return palier !== 'mvp';
}
/** Un nom de palier de déblocage, et pas une chaîne quelconque. */
export function estPalierModule(valeur) {
    return typeof valeur === 'string' && PALIERS_MODULE.includes(valeur);
}
// ── Modules ──────────────────────────────────────────────────────────────────
export const FAMILLES_MODULE = ['coeur', 'complementaire', 'service', 'addon'];
/**
 * Les catégories d'affichage de la console (hors cœur), dans l'ordre où elles
 * s'y présentent. Un classement de lecture : il n'a aucun effet sur l'accès.
 */
export const CATEGORIES_MODULE = [
    'ventes-clients',
    'oeuvres-documents',
    'artistes-contrats',
    'diffusion',
    'equipe-integrations',
    'ia',
    'gestion',
];
/** Les modules du cœur : toujours actifs, pour toutes les galeries. */
export const MODULES_COEUR = [
    'tableau-de-bord',
    'oeuvres',
    'artistes',
    'contacts',
    'expositions',
    'documents-express',
    'equipe',
    'deux-facteurs',
    'reglages',
    'export',
    'profil',
    'exploitation',
    'conservation-legale',
    'etiquettes',
];
/** Les modules complémentaires de la page commerciale, dans son ordre. */
export const MODULES_COMPLEMENTAIRES = [
    'facturation',
    'viewing-rooms',
    'coffre',
    'offres-relances',
    'mode-foire',
    'documents-prestige',
    'modeles-contrats',
    'mouvements',
    'editions-provenance',
    'contrats-artistes',
    'agenda-presse',
    'roles-fins',
    'newsletter-reseaux',
    'artsy-artnet',
    'application-hors-ligne',
    'api-comptabilite',
    // Absents de la page commerciale, mais livrés : la console de groupe et
    // l’authentification unique.
    'console-groupe',
    'sso',
];
/** Les modules liés à un service vendu par Venio. */
export const MODULES_SERVICES = ['site', 'boutique'];
/** Complémentaires et services : ce qu'une liste `modulesActifs` peut contenir. */
export const MODULES_OPTIONNELS = [...MODULES_COMPLEMENTAIRES, ...MODULES_SERVICES];
/** Add-ons : droits datés et publication, jamais modulesActifs. */
export const MODULES_ADDONS = [
    'pipeline-ventes',
    'profils-collectionneurs',
    'espace-collectionneur',
    'evenements',
    'catalogue-visite',
    'rendez-vous-client',
    'prise-rendez-vous',
    'boite-reception',
    'assistant-reponse',
    'catalogage-image',
    'studio-contenu',
    'assistant-mcp',
    'assistant-recherche',
    'reserve-inventaire',
    'plan-accrochage',
    'valeurs-assurance',
    'comptes-artistes',
    'paiement-en-ligne',
    'tresorerie',
    'registre-police',
    'location-oeuvres',
    'espace-deposant',
    'pilotage',
    'signature-electronique',
];
export const moduleAddonSchema = z.enum(MODULES_ADDONS);
export const MODULES_PILOTABLES = [...MODULES_OPTIONNELS, ...MODULES_ADDONS];
export const MODULES = [...MODULES_COEUR, ...MODULES_PILOTABLES];
export const moduleSchema = z.enum(MODULES);
export const moduleOptionnelSchema = z.enum(MODULES_OPTIONNELS);
function libelles(id) {
    return {
        libelle: `commun:modules.${id}.libelle`,
        description: `commun:modules.${id}.description`,
    };
}
const RIEN = {
    routes: [],
    navigation: [],
    travaux: [],
    blocsSite: [],
    scopes: [],
};
function addon(id, categorie) {
    return {
        id,
        famille: 'addon',
        categorie,
        disponible: false,
        dependances: id === 'assistant-reponse' ? ['boite-reception'] : [],
        ...libelles(id),
        ...RIEN,
    };
}
function coeur(id) {
    return {
        id,
        famille: 'coeur',
        palier: 'mvp',
        disponible: true,
        dependances: [],
        ...libelles(id),
        ...RIEN,
    };
}
function complementaire(id, disponible, categorie, palier) {
    return {
        id,
        famille: 'complementaire',
        categorie,
        palier,
        disponible,
        dependances: [],
        ...libelles(id),
        ...RIEN,
    };
}
/** Le registre, dans l'ordre d'affichage. */
export const REGISTRE_MODULES = {
    // ── Cœur (page : tableau `core`) ─────────────────────────────────────────
    'tableau-de-bord': {
        ...coeur('tableau-de-bord'),
        // Le tableau de bord et la recherche instantanée (⌘K).
        routes: ['/tableau-de-bord', '/infos'],
        navigation: [''],
    },
    oeuvres: {
        ...coeur('oeuvres'),
        // Fiche, localisation, import et export avec images, prix artiste et
        // marge ; et la VENTE SIMPLE d'une œuvre (`…/oeuvres/:id/vente`), sans
        // facture.
        routes: ['/oeuvres', '/medias', '/lieux'],
        navigation: ['oeuvres'],
        // La reprise d'images (ARTO-131) : le rapprochement des fichiers d'un
        // dépôt avec les œuvres, puis leur rattachement, sous ce module.
        travaux: [
            'media:variants',
            'medias:rappels-droits',
            'import:oeuvres',
            'import:lieux',
            'reprise:rapprocher',
            'import:images',
            // L'historique des localisations (ARTO-133) : au cœur, car sans le
            // module `mouvements` la localisation courante est reprise quand même.
            'import:localisations',
        ],
        scopes: ['read:works', 'write:works', 'read:media', 'write:media'],
    },
    artistes: {
        ...coeur('artistes'),
        routes: ['/artistes'],
        navigation: ['artistes'],
        travaux: ['import:artistes'],
        scopes: ['read:artists', 'write:artists'],
    },
    contacts: {
        ...coeur('contacts'),
        // Carnet, demandes, consentement et RGPD, portabilité ; et les
        // réservations (« réservation et vente d'une œuvre »).
        routes: ['/contacts', '/demandes', '/reservations'],
        navigation: ['contacts'],
        travaux: ['reservations:expire', 'contacts:effacement-brevo', 'import:contacts'],
        scopes: ['read:contacts', 'write:contacts'],
    },
    expositions: {
        ...coeur('expositions'),
        // Expositions et foires, œuvres accrochées, vues d'accrochage.
        routes: ['/expositions'],
        navigation: ['expositions'],
        travaux: ['import:expositions'],
        scopes: ['read:exhibitions', 'write:exhibitions'],
    },
    'documents-express': {
        ...coeur('documents-express'),
        // Fiche d'œuvre en PDF et liste de prix en un clic, au modèle par défaut ;
        // la portabilité d'un contact en PDF.
        routes: [
            '/oeuvres/:id/fiche.pdf',
            '/expositions/:id/liste-de-prix.pdf',
            '/expositions/:id/documents',
            '/contacts/:id/export.pdf',
            '/ventes/liste-de-prix',
            '/ventes/liste-de-prix.pdf',
        ],
        travaux: ['document:render'],
        scopes: ['read:documents', 'write:documents'],
    },
    equipe: {
        ...coeur('equipe'),
        // Plusieurs utilisateurs, rôles simples ; accès de support ; mandat de
        // reprise accordé à Venio (ARTO-125).
        routes: ['/equipe', '/support-access', '/mandat-reprise'],
        // La reprise des membres (ARTO-133) : des invitations, jamais de mot de passe.
        travaux: ['import:membres'],
        scopes: ['read:members', 'write:members'],
    },
    'deux-facteurs': {
        ...coeur('deux-facteurs'),
        // Les routes du second facteur vivent sous `/v1/auth/2fa`, hors galerie.
        scopes: ['read:2fa', 'manage:2fa'],
    },
    reglages: {
        ...coeur('reglages'),
        travaux: ['ia:liberer-reservations'],
        // Journal des modifications et numérotation.
        routes: [
            '/activity',
            '/sequences',
            '/connecteurs',
            '/reglages/addons',
            '/reglages/facturation-artosera',
            // Activer ses modules complémentaires (ARTO-193) : au cœur, sinon un
            // module coupé fermerait la page qui le rallume.
            '/reglages/modules',
            '/notifications',
            '/ia',
            // La demande de création de site : au cœur, puisqu'elle s'adresse
            // justement aux galeries qui n'ont pas encore le service `site`.
            '/demande-site',
        ], // Connecteur (M0) : la configuration suit le module.
        navigation: ['reglages'],
        scopes: ['read:audit', 'write:audit'],
    },
    export: {
        ...coeur('export'),
        // L'export complet inclut TOUJOURS toutes les données, modules inactifs
        // compris : la portabilité ne se vend pas en option (7.7).
        travaux: ['export:full'],
        scopes: ['manage:export'],
    },
    profil: coeur('profil'),
    exploitation: {
        ...coeur('exploitation'),
        // Ce que l'exploitant fait tourner pour chaque galerie, sans qu'elle le
        // voie : sauvegardes, mesure du stockage, purge après résiliation.
        travaux: [
            'storage:measure',
            'purge:deferred',
            'backup:nightly',
            'backup:restore-test',
            'backup:prune',
            // Le contrôle d'effacement après une purge et le ménage des comptes
            // sans galerie (ARTO-54).
            'purge:controle-effacement',
            'comptes:orphelins',
            // La rétention de la file de travaux (ARTO-137).
            'travaux:purge',
            // Retirer le site servi d'une galerie dont le service `site` est coupé
            // (D21, ARTO-56) : il doit tourner justement quand `site` ne tourne plus.
            'site:depublier',
            // La convergence périodique des sites servis avec les modules (F1–F2).
            'site:reconcilier',
            // La vue d'aigle de la console (ARTO-58) : le compte des œuvres de
            // chaque galerie et la photo quotidienne de la plateforme.
            'usage:measure',
            'overview:snapshot',
            'marketplace:echeances',
            'marketplace:renouveler',
            'facturation:renouveler-coeur',
            'marketplace:synchroniser-paiements',
            'marketplace:rendre-facture',
            'marketplace:envoyer-facture',
            'marketplace:rattraper-factures',
            // Les préréglages d'un dossier d'ouverture (ARTO-127) : une étape du
            // provisionnement, transverse, sous le cœur.
            'ouverture:prereglages',
            // Le relevé mensuel des associés, à la clôture d'un mois comptable.
            'comptabilite:releves',
            // Son rattrapage : enfilement refusé, relevé resté en échec.
            'comptabilite:releves-rattrapage',
            // M10 : retirer des dépôts même quand le module de publication est coupé.
            'diffusion:reconcilier-retraits',
            'boutique:liquider',
            'signature:liquider',
            // L'essai d'un lot de reprise de la console (ARTO-125), toutes natures :
            // un outil de plateforme (P5). Le module de la nature s'y ajoute par la
            // charge (`modulesRequis`).
            'reprise:essayer',
            // Le dépôt industriel (ARTO-129) : l'inventaire d'un dépôt (empreintes,
            // type réel, antivirus) et la purge de ses sources après la bascule.
            'reprise:inventorier',
            'reprise:purger',
            // Le connecteur d'une application d'origine (ARTO-135) : il remplit un
            // dépôt, toutes natures ; transverse, sous le cœur.
            'reprise:connecteur',
            // La correspondance (ARTO-130) : profiler les sources d'un lot, puis les
            // transformer en pivot. Transverses, toutes natures : sous le cœur. Ils
            // n'écrivent que le stockage de la reprise, jamais la base de la galerie.
            'reprise:profiler',
            'reprise:transformer',
            // L'orchestration (ARTO-132) : le plan, qui lance les lots dans l'ordre
            // des natures, et l'annulation d'un lot — un outil de plateforme, sous le
            // cœur, quel que soit le module de la nature (P5).
            'reprise:planifier',
            'reprise:annuler',
            // Le procès-verbal de la reprise validée par la galerie (ARTO-134) :
            // sa pièce, rangée au coffre seulement si le module y est.
            'reprise:pv',
        ],
        scopes: [
            'read:backup',
            'manage:backup',
            'read:automations',
            'write:automations',
            'trigger:automations',
        ],
    },
    'conservation-legale': {
        ...coeur('conservation-legale'),
        // Les factures et avoirs ÉMIS, en lecture seule, dix ans (Code de
        // commerce, art. L123-22) — que `facturation` soit actif ou non. Un
        // routeur à part, dont les requêtes portent le prédicat de conservation.
        routes: ['/archive-factures', '/archive-registre-police'],
        navigation: ['archive-factures', 'archive-registre-police'],
        travaux: ['import:factures-historiques', 'registre-police:purge'],
        // La lecture du registre survit à l'add-on, comme l'obligation de le présenter.
        scopes: ['read:police-register'],
    },
    etiquettes: {
        ...coeur('etiquettes'),
        // Étiquettes QR des œuvres (rouleau, planche A4) et cartels muraux, depuis
        // la liste ou la fiche (retours client du 21 oct. 2026). Les scopes sont
        // ceux des œuvres ; une étiquette d'emplacement ou de caisse exige en plus
        // l'add-on `reserve-inventaire`, contrôlé par la route.
        routes: ['/etiquettes', '/cartels'],
        gestes: ['service public `/cartel/<jeton>` : fiche publique de l’œuvre du cartel mural'],
    },
    // ── Complémentaires (page : tableau `modules`) ───────────────────────────
    facturation: {
        ...complementaire('facturation', true, 'ventes-clients', 'v1'),
        // Factures, avoirs, Factur-X, règlements, profil fiscal, export comptable.
        routes: [
            '/ventes',
            '/ventes/factures',
            '/ventes/reglements',
            '/ventes/export',
            '/ventes/profil-fiscal',
        ],
        navigation: ['ventes'],
        travaux: ['invoice:document'],
        // `write:invoices` est propre à la facturation : retiré d'un jeton quand
        // elle est coupée. `read:invoices` reste : il ouvre aussi l'archive légale
        // (`conservation-legale`, cœur), que la facturation soit active ou non.
        scopes: ['write:invoices'],
    },
    'viewing-rooms': {
        ...complementaire('viewing-rooms', true, 'ventes-clients', 'v2'),
        // Salles privées pour un collectionneur : lien expirant, prix visibles ou
        // non, ouvertures suivies. La salle se sert sur `/vr/<jeton>` (rendu à la
        // demande, D2), hors de `/v1/g` : sa garde est dans `viewing-rooms/public/`.
        // AUCUNE dépendance à `site` : sans le service, la salle se sert sur le
        // sous-domaine Artosera de la galerie (docs/dev/viewing-rooms.md § 2).
        routes: ['/viewing-rooms'],
        navigation: ['viewing-rooms'],
        // Gardé pour la réconciliation des sites publiés (modules.test.ts) : aucune
        // salle n'est publiée, le bloc ne rend rien.
        blocsSite: ['viewing-rooms'],
        scopes: ['read:viewing-rooms', 'write:viewing-rooms'],
        gestes: ['salle publique `/vr/<jeton>` : page, mot de passe, images, demande'],
    },
    coffre: {
        ...complementaire('coffre', true, 'oeuvres-documents', 'v1'),
        // Documents rattachés, échéances et rappels, journal des consultations.
        routes: ['/documents'],
        navigation: ['coffre'],
        travaux: ['documents:rappels', 'import:documents'],
        scopes: ['read:vault', 'write:vault'],
    },
    'offres-relances': {
        ...complementaire('offres-relances', true, 'ventes-clients', 'v2'),
        // Offres à un contact, envoi et suivi, relances ; historique d'achats et
        // d'intérêts par contact ; l'offre faite au stand (mode foire).
        routes: ['/offres'],
        navigation: ['offres'],
        travaux: ['offres:relances'],
        scopes: ['read:offers', 'write:offers'],
        gestes: ['offre faite au stand (POST …/offres, opération rejouable)'],
    },
    'mode-foire': {
        ...complementaire('mode-foire', true, 'ventes-clients', 'v2'),
        // Le stand vit dans la régie (et sa file hors ligne) ; il appelle les
        // routes du cœur. « Facturer » au stand exige en plus `facturation`.
        navigation: ['stand'],
    },
    'documents-prestige': {
        ...complementaire('documents-prestige', true, 'oeuvres-documents', 'v1'),
        // Certificats numérotés, dossier d'œuvres, modèles aux couleurs de la
        // galerie — l'écran d'édition des modèles compris. Coupé, la fiche et la
        // liste de prix du cœur prennent le modèle par défaut.
        routes: [
            '/oeuvres/:id/certificat',
            '/oeuvres/:id/certificats',
            '/ventes/modeles',
            '/dossiers-oeuvres',
            '/artistes/:id/cv',
            '/artistes/:id/dossier',
        ],
        navigation: ['reglages/documents'],
        gestes: ['modèle personnalisé au rendu des documents'],
    },
    'modeles-contrats': {
        ...complementaire('modeles-contrats', true, 'artistes-contrats', 'v1'),
        routes: ['/modeles-contrats'],
        navigation: ['contrats'],
        scopes: ['read:contracts', 'write:contracts'],
    },
    mouvements: {
        ...complementaire('mouvements', true, 'oeuvres-documents', 'v2'),
        // Prêts, consignations et dépôts avec leurs bons, transports, constats.
        // `/bons-depot` : l'ancien nom du module, gardé en lecture (liste des bons).
        routes: ['/mouvements', '/bons-depot'],
        navigation: ['mouvements'],
        travaux: ['mouvements:rappels'],
        scopes: ['read:movements', 'write:movements'],
    },
    'editions-provenance': {
        ...complementaire('editions-provenance', true, 'oeuvres-documents', 'v1'),
        routes: ['/oeuvres/:id/provenance', '/oeuvres/:id/couts', '/medias/:id/droits'],
        // La reprise de la provenance et des exemplaires (ARTO-133).
        travaux: ['import:provenance', 'import:editions'],
        // Éditions, tirages, exemplaires, épreuves, provenance. Coupé, les données
        // restent LISIBLES par les routes des œuvres ; seules la création d'une
        // édition et la génération d'exemplaires sont refusées.
        gestes: [
            'œuvre de type « edition » ou portant un tirage (POST et PATCH …/oeuvres)',
            'exemplaires (POST …/oeuvres/:id/editions)',
        ],
    },
    'contrats-artistes': {
        ...complementaire('contrats-artistes', true, 'artistes-contrats', 'v1'),
        routes: ['/contrats-artistes'],
        navigation: ['artistes/contrats'],
        travaux: ['contrats-artistes:rappels'],
        scopes: ['read:artist-contracts', 'write:artist-contracts'],
    },
    'agenda-presse': {
        ...complementaire('agenda-presse', true, 'diffusion', 'finale'),
        routes: ['/agenda', '/presse'],
        navigation: ['agenda', 'presse'],
        travaux: ['agenda:rappels'],
        scopes: ['read:agenda', 'write:agenda', 'read:press', 'write:press'],
        gestes: ['flux iCal privé (/v1/public/:slug/agenda/:jeton.ics)'],
    },
    'roles-fins': {
        ...complementaire('roles-fins', true, 'equipe-integrations', 'v1'),
        // Droits fins (qui voit le prix artiste, qui ouvre le coffre, qui émet,
        // qui publie). Coupé, les rôles simples s'appliquent avec leurs droits par
        // défaut ; les droits posés sont ignorés, non modifiables, et CONSERVÉS.
        // Le rôle simple (`/equipe/:id/role`) reste au cœur (`equipe`).
        routes: ['/equipe/:id/droits'],
        gestes: ['droits fins appliqués aux appartenances', 'modification des droits fins'],
    },
    'newsletter-reseaux': {
        ...complementaire('newsletter-reseaux', true, 'diffusion', 'finale'),
        // Listes par type de contact, campagnes envoyées par Brevo, statistiques,
        // visuels pour les réseaux. L'inscription depuis le site appartient au
        // service `site` ; le consentement, sa révocation (webhook Brevo) et
        // l'effacement RGPD vers Brevo appartiennent au cœur (`contacts`).
        routes: ['/newsletter', '/connecteurs/brevo'],
        navigation: ['newsletter'],
        travaux: ['brevo:sync', 'newsletter:envoi', 'newsletter:statistiques', 'newsletter:visuel'],
        scopes: ['read:newsletter', 'write:newsletter', 'send:newsletter'],
    },
    'artsy-artnet': {
        ...complementaire('artsy-artnet', true, 'diffusion', 'finale'),
        // Œuvres disponibles publiées vers Artsy et Artnet, par flux de fichier
        // (CSV, XML) récupéré à une adresse à jeton ou déposé. Réglages ›
        // Diffusion et l'onglet « Diffusion » de la fiche œuvre.
        routes: ['/diffusion', '/connecteurs/artsy', '/connecteurs/artnet'],
        navigation: ['reglages/diffusion'],
        travaux: ['diffusion:sync'],
        scopes: ['read:diffusion', 'write:diffusion'],
    },
    'application-hors-ligne': {
        ...complementaire('application-hors-ligne', true, 'equipe-integrations', 'v2'),
        // Aucune route d'API : le module se garde dans la régie, sur la foi de
        // `horsLigne` que `/v1/auth/me` rend par galerie (`horsLigne.ts`).
        navigation: ['reglages/hors-ligne'],
        gestes: [
            'réglage `horsLigne` de `/v1/auth/me` (périmètre et durée, `null` coupé)',
            'fiches gardées chiffrées sur l’appareil, service worker de la régie',
        ],
    },
    'api-comptabilite': {
        ...complementaire('api-comptabilite', true, 'equipe-integrations', 'finale'),
        // Jetons d'agent émis et révoqués par la galerie, description OpenAPI,
        // plan comptable et fichier FEC (M8, décision n° 7 : aucun connecteur
        // propriétaire). Coupé, un jeton d'agent de la galerie est refusé — sauf
        // un jeton d'administration de Venio. AUCUNE dépendance : le FEC ne lit
        // que des pièces émises, conservées par le cœur (`conservation-legale`).
        routes: ['/acces-api', '/comptabilite'],
        navigation: ['reglages/acces-api'],
        scopes: ['read:accounting', 'write:accounting'],
        gestes: [
            'jetons d’agent sur les routes de galerie et `/v1/agent`',
            'FEC des pièces émises, que `facturation` soit active ou non',
        ],
    },
    'console-groupe': {
        ...complementaire('console-groupe', true, 'equipe-integrations', 'finale'),
        routes: ['/groupe'],
        navigation: ['groupe'],
    },
    sso: {
        ...complementaire('sso', true, 'equipe-integrations', 'finale'),
        // Le connecteur OIDC de la galerie (M0) ; la connexion elle-même vit hors galerie (M13).
        routes: ['/connecteurs/oidc', '/sso'],
    },
    // ── Services (page : tableau `services`) ─────────────────────────────────
    site: {
        id: 'site',
        famille: 'service',
        categorie: 'diffusion',
        palier: 'finale',
        disponible: true,
        dependances: [],
        ...libelles('site'),
        ...RIEN,
        // « Nouveau site de la galerie » : tout le site public — réglages, thème,
        // pages, actualités, aperçu, publication, formulaires publics et
        // inscription à la newsletter depuis le site. Artosera ne touche jamais au
        // site actuel d'une galerie qui n'a pas acheté ce service.
        routes: ['/site', '/actualites'],
        navigation: ['site'],
        travaux: ['site:publish', 'site:links'],
        blocsSite: ['newsletter'],
        scopes: ['read:site', 'write:site', 'publish:site'],
    },
    boutique: {
        id: 'boutique',
        famille: 'service',
        categorie: 'ventes-clients',
        palier: 'finale',
        disponible: true,
        dependances: ['site', 'facturation'],
        ...libelles('boutique'),
        ...RIEN,
        navigation: ['boutique'],
        travaux: ['boutique:expirer'],
        scopes: ['read:shop', 'write:shop'],
        gestes: ['achat public', 'webhook paiement : liquidation seule après coupure'],
        routes: ['/boutique', '/connecteurs/stripe'], // Connecteur (M0) : la configuration suit le module.
        blocsSite: ['boutique'],
    },
    'pipeline-ventes': {
        ...addon('pipeline-ventes', 'ventes-clients'),
        disponible: true,
        routes: ['/affaires', '/listes-attente', '/pipeline'],
        navigation: ['pipeline'],
        scopes: ['read:pipeline', 'write:pipeline'],
        gestes: [
            'offre créée : lien et avancement sans recul',
            'offre refusée, retirée ou expirée : proposition de perte',
            'réservation créée : lien et avancement sans recul',
            'facture émise ou vente simple : gain et perte des autres affaires',
            'vente : clôture des listes d’attente',
            'réservation levée ou expirée : rappel à la direction et affaire depuis la liste d’attente',
        ],
    },
    'profils-collectionneurs': {
        ...addon('profils-collectionneurs', 'ventes-clients'),
        disponible: true,
        routes: ['/profils', '/contacts/:id/profil', '/contacts/:id/score', '/oeuvres/:id/a-prevenir'],
        navigation: ['reglages/profils'],
        travaux: ['profils:score', 'profils:correspondances'],
        scopes: ['read:profiles', 'write:profiles'],
        gestes: [
            'colonne et tri par score de la liste des contacts',
            'bloc « À prévenir » du tableau de bord',
            'déclenchement des correspondances depuis les œuvres et les réservations',
        ],
    },
    'espace-collectionneur': {
        ...addon('espace-collectionneur', 'ventes-clients'),
        disponible: true,
        routes: ['/espace-collectionneur'],
        navigation: ['espaces-collectionneurs', 'reglages/espace-collectionneur'],
        scopes: ['read:collector-space', 'write:collector-space'],
        travaux: [],
        gestes: [
            'service public `/espace` : connexion par lien, œuvres, documents, conseils de conservation, message à la galerie',
        ],
    },
    evenements: {
        ...addon('evenements', 'ventes-clients'),
        disponible: true,
        routes: ['/evenements'],
        navigation: ['evenements'],
        travaux: ['evenements:rappels', 'evenements:messages'],
        blocsSite: ['evenements'],
        scopes: ['read:events', 'write:events'],
        gestes: [
            'pages publiques /inscription/<jeton> et /billet/<jeton>',
            'encarts exposition et contact',
            'source de l’agenda',
        ],
    },
    'catalogue-visite': {
        ...addon('catalogue-visite', 'ventes-clients'),
        disponible: true,
        routes: ['/expositions/:id/catalogue-visite', '/catalogues-visite'],
        navigation: [],
        scopes: ['read:visit-catalogue', 'write:visit-catalogue'],
        gestes: ['service public `/visite/<jeton>` : accueil, fiches, images, demande'],
    },
    'rendez-vous-client': {
        ...addon('rendez-vous-client', 'ventes-clients'),
        disponible: true,
        routes: ['/selections', '/negociation', '/oeuvres/:id/plancher'],
        navigation: ['presentation', 'reglages/negociation'],
        scopes: ['read:presentation', 'write:presentation', 'approve:negotiation'],
        gestes: ['prix approuvé proposé à la création d’une offre, d’une réservation ou d’une facture'],
    },
    'prise-rendez-vous': {
        ...addon('prise-rendez-vous', 'ventes-clients'),
        disponible: true,
        routes: ['/prise-rendez-vous'],
        navigation: ['rendez-vous-en-ligne', 'reglages/rendez-vous-en-ligne'],
        travaux: ['prise-rendez-vous:rappels', 'prise-rendez-vous:entretien'],
        blocsSite: ['prise-rendez-vous'],
        scopes: ['read:bookings', 'write:bookings'],
        gestes: [
            'service public `/rendez-vous/` : créneaux, réservation, vérification, gestion',
            'rendez-vous de l’agenda créé, déplacé ou annulé avec la réservation',
            'lien « Demander une présentation » sur la fiche d’œuvre du site',
        ],
    },
    'boite-reception': {
        ...addon('boite-reception', 'diffusion'),
        disponible: true,
        routes: ['/boite-reception'],
        navigation: ['boite-reception', 'boite-reception/delais', 'reglages/boite-reception'],
        travaux: [
            'boite-reception:traiter-entrant',
            'boite-reception:resume',
            'boite-reception:synchroniser',
        ],
        scopes: ['read:inbox', 'write:inbox'],
        gestes: [
            'réception e-mail signée (route publique)',
            'pixel d’ouverture (route publique)',
            'blocs Échanges des fiches contact et œuvre',
            'demande répondue depuis la boîte',
        ],
    },
    'assistant-reponse': {
        ...addon('assistant-reponse', 'ia'),
        disponible: true,
        routes: ['/assistant-reponse'],
        navigation: ['boite-reception/assistant', 'reglages/assistant-reponse'],
        travaux: [
            'assistant-reponse:trier',
            'assistant-reponse:brouillon',
            'assistant-reponse:relances',
        ],
    },
    'catalogage-image': {
        ...addon('catalogage-image', 'ia'),
        disponible: true,
        routes: ['/catalogage'],
        navigation: ['oeuvres/catalogage'],
        travaux: ['catalogage-image:serie', 'catalogage-image:menage'],
    },
    // Studio : livraison sans dépendance ni travail asynchrone.
    'studio-contenu': {
        ...addon('studio-contenu', 'ia'),
        disponible: true,
        routes: ['/studio'],
        navigation: ['studio'],
        scopes: ['read:studio'],
    },
    'assistant-mcp': {
        ...addon('assistant-mcp', 'ia'),
        disponible: true,
        routes: ['/assistant-mcp'],
        navigation: ['reglages/assistant-mcp'],
        gestes: [
            'serveur MCP `/v1/agent/mcp` : relais des routes de galerie par un accès assistant',
            'accès assistant refusé hors du serveur MCP ; sans `api-comptabilite`',
        ],
    },
    'assistant-recherche': {
        ...addon('assistant-recherche', 'ia'),
        disponible: true,
        routes: ['/assistant-recherche'],
        navigation: ['demander', 'reglages/assistant-recherche'],
        gestes: ['relais en lecture des routes du catalogue MCP, avec l’identité du membre'],
    },
    'reserve-inventaire': {
        ...addon('reserve-inventaire', 'oeuvres-documents'),
        disponible: true,
        routes: ['/emplacements', '/caisses', '/inventaires'],
        navigation: ['inventaire', 'reglages/emplacements'],
        travaux: [],
        scopes: ['read:storage', 'write:storage'],
        gestes: [
            'étiquettes d’emplacements et de caisses (routes `/etiquettes` du cœur)',
            'emplacement et caisse sur la fiche de l’œuvre',
            'caisses citées par un mouvement et listées sur son bon',
            'œuvres introuvables au tableau de bord',
        ],
    },
    'plan-accrochage': {
        ...addon('plan-accrochage', 'oeuvres-documents'),
        disponible: true,
        routes: ['/espaces', '/plans-accrochage'],
        navigation: ['accrochage'],
        scopes: ['read:hanging', 'write:hanging'],
    },
    'valeurs-assurance': {
        ...addon('valeurs-assurance', 'gestion'),
        disponible: true,
        routes: ['/estimations', '/polices', '/assurance', '/oeuvres/:id/estimations'],
        navigation: ['assurance', 'reglages/assurance'],
        travaux: ['assurance:reevaluations'],
        scopes: ['read:insurance', 'write:insurance'],
        gestes: ['proposition de valeur assurée dans un bon de prêt (mouvements)'],
    },
    'comptes-artistes': {
        ...addon('comptes-artistes', 'gestion'),
        disponible: true,
        dependances: ['facturation'],
        routes: ['/comptes-artistes', '/avances', '/artistes/:id/compte'],
        navigation: ['comptes-artistes'],
        travaux: ['comptes-artistes:rapprocher', 'comptes-artistes:releves-periodiques'],
        scopes: ['read:artist-accounts', 'write:artist-accounts'],
    },
    'paiement-en-ligne': {
        ...addon('paiement-en-ligne', 'gestion'),
        disponible: false,
        dependances: ['facturation'],
        routes: ['/paiement-en-ligne', '/ventes/factures/:id/paiement-en-ligne'],
        navigation: ['paiement-en-ligne'],
        travaux: ['paiement-en-ligne:synchroniser', 'paiement-en-ligne:relances'],
        scopes: ['read:online-payments'],
    },
    tresorerie: {
        ...addon('tresorerie', 'gestion'),
        disponible: true,
        dependances: ['facturation'],
        routes: ['/tresorerie'],
        navigation: ['tresorerie', 'reglages/tresorerie'],
        travaux: ['tresorerie:rapprocher'],
        scopes: ['read:treasury'],
    },
    'registre-police': {
        ...addon('registre-police', 'oeuvres-documents'),
        disponible: true,
        routes: ['/registre-police'],
        navigation: ['registre-police'],
        travaux: ['registre-police:cloture'],
        scopes: ['write:police-register'],
    },
    'espace-deposant': {
        ...addon('espace-deposant', 'gestion'),
        disponible: true,
        dependances: ['mouvements'],
        routes: ['/oeuvres-confiees'],
        navigation: ['depots-recus'],
        scopes: ['read:consignments', 'write:consignments'],
    },
    pilotage: {
        ...addon('pilotage', 'gestion'),
        disponible: true,
        routes: ['/pilotage'],
        navigation: ['pilotage'],
        scopes: ['read:insights', 'write:insights'],
    },
    'signature-electronique': {
        ...addon('signature-electronique', 'oeuvres-documents'),
        disponible: true,
        routes: ['/signatures', '/connecteurs/yousign'],
        navigation: ['signatures'],
        scopes: ['read:signatures', 'write:signatures'],
    },
    'location-oeuvres': {
        ...addon('location-oeuvres', 'gestion'),
        disponible: true,
        dependances: ['facturation'],
        routes: ['/locations', '/oeuvres/:id/locations'],
        navigation: ['locations'],
        travaux: ['locations:echeances'],
        scopes: ['read:rentals', 'write:rentals'],
        gestes: [
            'vente d’une œuvre louée : seulement par la levée de son option d’achat',
            'onglet Locations de la fiche œuvre, encart Contrats de la fiche contact',
        ],
    },
};
/**
 * Les modules hors cœur par catégorie, dans l'ordre d'affichage de la console.
 * Chaque module y figure une fois, dans la catégorie que déclare le registre
 * (`categorie`) : un test de `shared` garde les deux d'accord.
 */
export const MODULES_PAR_CATEGORIE = {
    'ventes-clients': [
        'facturation',
        'offres-relances',
        'viewing-rooms',
        'mode-foire',
        'boutique',
        'pipeline-ventes',
        'profils-collectionneurs',
        'espace-collectionneur',
        'evenements',
        'catalogue-visite',
        'rendez-vous-client',
        'prise-rendez-vous',
    ],
    'oeuvres-documents': [
        'coffre',
        'documents-prestige',
        'editions-provenance',
        'mouvements',
        'reserve-inventaire',
        'plan-accrochage',
        'signature-electronique',
        'registre-police',
    ],
    'artistes-contrats': ['contrats-artistes', 'modeles-contrats'],
    diffusion: ['site', 'newsletter-reseaux', 'agenda-presse', 'artsy-artnet', 'boite-reception'],
    'equipe-integrations': [
        'roles-fins',
        'sso',
        'console-groupe',
        'api-comptabilite',
        'application-hors-ligne',
    ],
    ia: [
        'assistant-reponse',
        'catalogage-image',
        'studio-contenu',
        'assistant-mcp',
        'assistant-recherche',
    ],
    gestion: [
        'valeurs-assurance',
        'comptes-artistes',
        'pilotage',
        'paiement-en-ligne',
        'tresorerie',
        'location-oeuvres',
        'espace-deposant',
    ],
};
/** La définition d'un module. */
export function definitionModule(module) {
    return REGISTRE_MODULES[module];
}
/** Toutes les définitions, dans l'ordre du registre. */
export const DEFINITIONS_MODULES = MODULES.map(definitionModule);
/** Un nom de module déclaré par le registre, et pas une chaîne quelconque. */
export function estModule(valeur) {
    return typeof valeur === 'string' && MODULES.includes(valeur);
}
export function estModuleCoeur(module) {
    return REGISTRE_MODULES[module].famille === 'coeur';
}
/** Un module hors du cœur — complémentaire ou lié à un service. */
export function estModuleOptionnel(valeur) {
    return estModule(valeur) && MODULES_OPTIONNELS.includes(valeur);
}
export function estModuleAddon(valeur) {
    return typeof valeur === 'string' && MODULES_ADDONS.includes(valeur);
}
/**
 * Un service (site, boutique) : une prestation commandée et réglée à part. Sans
 * achat, aucun accès, pas même en lecture seule (décision du 27 octobre 2026).
 */
export function estModuleService(valeur) {
    return typeof valeur === 'string' && MODULES_SERVICES.includes(valeur);
}
export function estModulePilotable(valeur) {
    return estModuleOptionnel(valeur) || estModuleAddon(valeur);
}
/** Un module optionnel construit, donc activable. */
export function estModuleActivable(valeur) {
    return estModuleOptionnel(valeur) && REGISTRE_MODULES[valeur].disponible;
}
/** Les modules qu'apporte un palier de déblocage, dans l'ordre du registre. */
export function modulesDuPalier(palier) {
    return MODULES.filter((module) => REGISTRE_MODULES[module].palier === palier);
}
/**
 * Les modules fermés par le verrou de palier de la plateforme, tels que
 * `GET /v1/auth/me` les annonce (`modulesVerrouilles`) : ni les modules coupés
 * en urgence (une coupure garde son affichage « module non activé », jamais une
 * fiche), ni les add-ons. La régie n'en tire que des modules à venir
 * (`modulesAVenir`), qu'elle regroupe par palier grâce au registre.
 */
export const modulesVerrouillesSchema = z.array(moduleOptionnelSchema);
/**
 * Les modules À VENIR d'une galerie : ceux que le verrou de palier ferme, que la
 * galerie n'a pas d'effectif, par palier puis dans l'ordre du registre.
 * Complémentaires et services seulement : le cœur est au MVP, les add-ons n'ont
 * pas de palier. Un module d'un palier DÉBLOQUÉ, souscrit ou non, n'en fait pas
 * partie : un module non acheté ne s'affiche pas. Un palier à moitié débloqué
 * (la facturation ouverte en avance) garde ses autres modules à venir.
 */
export function modulesAVenir(modulesVerrouilles, effectifs) {
    const verrouilles = new Set(modulesVerrouilles);
    return PALIERS_MODULE.flatMap((palier) => modulesDuPalier(palier).filter((module) => estModuleOptionnel(module) && verrouilles.has(module) && !effectifs.includes(module)));
}
/** Tous les modules optionnels disponibles : la liste « tout activé ». */
export function modulesDisponibles() {
    return MODULES_OPTIONNELS.filter((module) => REGISTRE_MODULES[module].disponible);
}
// ── La fonction unique ───────────────────────────────────────────────────────
export const MODES_ADDON = ['achat', 'location', 'essai', 'offert'];
export const ETATS_PUBLICATION_ADDON = ['essai', 'publiee', 'fermee'];
export const DELAI_LECTURE_SEULE_ADDON_JOURS = 14;
export function droitAddonEnCours(droit, maintenant) {
    return (new Date(droit.depuisLe).getTime() <= maintenant &&
        (droit.jusquAu === null
            ? droit.mode === 'achat' || droit.mode === 'offert'
            : maintenant < new Date(droit.jusquAu).getTime()));
}
export function publicationAddon(addon, publications) {
    return publications.find((ligne) => ligne.addon === addon)?.etat ?? 'essai';
}
export function publicationAddonVisible(addon, publications, estTest) {
    const etat = publicationAddon(addon, publications);
    return etat === 'publiee' || (etat === 'essai' && estTest);
}
/**
 * LES MODULES D'UNE GALERIE — la seule fonction qui les calcule (D21).
 *
 *   cœur ∪ (modulesActifs ∩ disponibles ∩ dépendances satisfaites)
 *        − modules coupés globalement
 *
 * `coupes` réunit l'interrupteur global de la base plateforme et la variable
 * de secours `ARTOSERA_MODULES_COUPES`. Un module du cœur n'y est jamais
 * pris en compte : il n'est pas désactivable. Les dépendances se résolvent
 * jusqu'au point fixe — couper `facturation` éteint aussi `boutique`.
 * Une valeur inconnue de la liste est ignorée, jamais levée : une base écrite
 * par une version antérieure ne doit pas fermer la galerie.
 */
export function modulesDeLaGalerie(galerie, coupes = [], maintenant, publications = []) {
    const coupe = new Set(coupes);
    const retenus = new Set();
    for (const valeur of galerie.modulesActifs ?? []) {
        if (!estModuleOptionnel(valeur))
            continue;
        if (!REGISTRE_MODULES[valeur].disponible)
            continue;
        if (coupe.has(valeur))
            continue;
        retenus.add(valeur);
    }
    const estPresent = (module) => estModuleCoeur(module) || retenus.has(module);
    // Sans horloge injectée, aucun droit n'est ouvert (anciens appelants).
    if (maintenant !== undefined) {
        for (const droit of galerie.droitsAddons ?? []) {
            const id = droit.addon;
            if (!estModuleAddon(id) || !REGISTRE_MODULES[id].disponible || coupe.has(id))
                continue;
            if (!droitAddonEnCours(droit, maintenant))
                continue;
            if (!publicationAddonVisible(id, publications, galerie.estTest === true))
                continue;
            retenus.add(id);
        }
    }
    let change = true;
    while (change) {
        change = false;
        for (const module of retenus) {
            if (!REGISTRE_MODULES[module].dependances.every(estPresent)) {
                retenus.delete(module);
                change = true;
            }
        }
    }
    return MODULES.filter((module) => estModuleCoeur(module) || retenus.has(module));
}
/**
 * Les modules EN LECTURE SEULE d'une galerie : consultables par un humain,
 * jamais modifiables (la garde de l'API refuse tout sauf GET et HEAD).
 *
 *   modulesLectureSeule ∩ pilotables disponibles − services − effectifs − coupés − verrouillés
 *
 * Une coupure d'urgence ferme tout, lecture comprise ; un module verrouillé
 * par son palier reste « à venir ». Un service (site, boutique) n'est jamais
 * consultable sans avoir été acheté (`estModuleService`). Valeur inconnue
 * ignorée, ordre du registre.
 */
export function modulesEnLectureSeule(galerie, effectifs, coupes = [], verrouilles = [], maintenant, publications) {
    const demandes = new Set(galerie.modulesLectureSeule ?? []);
    const exclus = new Set([...effectifs, ...coupes, ...verrouilles]);
    for (const droit of galerie.droitsAddons ?? []) {
        if (maintenant === undefined || droit.jusquAu === null)
            continue;
        const fin = new Date(droit.jusquAu).getTime();
        if (fin <= maintenant && maintenant < fin + DELAI_LECTURE_SEULE_ADDON_JOURS * 86_400_000)
            demandes.add(droit.addon);
    }
    const retenus = new Set(MODULES_PILOTABLES.filter((module) => {
        if (!demandes.has(module) ||
            !REGISTRE_MODULES[module].disponible ||
            estModuleService(module) ||
            exclus.has(module))
            return false;
        if (!estModuleAddon(module))
            return true;
        return (maintenant !== undefined &&
            publicationAddonVisible(module, publications, galerie.estTest === true));
    }));
    // Une dépendance doit rester effective ou elle-même consultable.
    let change = true;
    while (change) {
        change = false;
        for (const module of retenus) {
            if (estModuleAddon(module) &&
                !REGISTRE_MODULES[module].dependances.every((id) => effectifs.includes(id) || retenus.has(id))) {
                retenus.delete(module);
                change = true;
            }
        }
    }
    return MODULES_PILOTABLES.filter((module) => retenus.has(module));
}
/** Une galerie dispose-t-elle de ce module ? Même calcul, même règle. */
export function hasModule(galerie, module, coupes = [], maintenant, publications = []) {
    return modulesDeLaGalerie(galerie, coupes, maintenant, publications).includes(module);
}
/**
 * La liste d'activation d'une galerie, nettoyée pour être ÉCRITE : modules
 * optionnels activables seulement, sans doublon, dans l'ordre du registre.
 */
export function normaliserModulesActifs(valeurs) {
    const ensemble = new Set(valeurs);
    return MODULES_OPTIONNELS.filter((module) => ensemble.has(module) && REGISTRE_MODULES[module].disponible);
}
/**
 * Les dépendances qui manquent pour activer un module, compte tenu d'une
 * liste d'activation. Vide quand tout est là.
 */
export function dependancesManquantes(module, modulesActifs) {
    const actifs = new Set(modulesActifs);
    return REGISTRE_MODULES[module].dependances.filter((dependance) => !estModuleCoeur(dependance) && !actifs.has(dependance));
}
/** Les modules qui dépendent (directement) de celui-ci. */
export function modulesDependantDe(module) {
    return MODULES.filter((candidat) => REGISTRE_MODULES[candidat].dependances.includes(module));
}
// ── Couverture : routes, navigation, travaux, blocs, scopes ──────────────────
function segments(chemin) {
    return chemin
        .split('?')[0]
        .split('/')
        .filter((segment) => segment.length > 0);
}
const RATTACHEMENTS_ROUTES = DEFINITIONS_MODULES.flatMap((definition) => definition.routes.map((route) => ({ module: definition.id, motif: segments(route) })));
/**
 * Le module d'une route API de galerie, par le préfixe le plus long qui la
 * couvre ; `null` si aucun. `chemin` est relatif à la galerie :
 * `/ventes/factures/:id/pdf`, `/oeuvres/66f…/fiche.pdf`.
 *
 * Les segments de PARAMÈTRE d'un chemin (`:id`) ne couvrent qu'un paramètre du
 * motif : le test d'exhaustivité passe des motifs de route Express, et
 * `/oeuvres/:id` ne doit pas être pris pour `/oeuvres/numero-inventaire`.
 */
export function moduleDeLaRoute(chemin) {
    const cible = segments(chemin);
    let meilleur = null;
    for (const rattachement of RATTACHEMENTS_ROUTES) {
        const ok = rattachement.motif.every((segment, rang) => {
            const valeur = cible[rang];
            if (valeur === undefined)
                return false;
            if (segment.startsWith(':'))
                return true;
            return !valeur.startsWith(':') && segment === valeur.toLowerCase();
        });
        if (!ok)
            continue;
        if (meilleur === null || rattachement.motif.length > meilleur.motif.length) {
            meilleur = rattachement;
        }
    }
    return meilleur?.module ?? null;
}
/**
 * Le module d'une entrée de la régie, par son chemin sous `/:slug/` : l'entrée
 * de navigation la plus longue qui en est un préfixe, segment par segment
 * (`reglages/documents` l'emporte sur `reglages`). L'entrée vide (`''`, le
 * tableau de bord) ne vaut que pour la racine.
 */
export function moduleDeLaNavigation(chemin) {
    const cible = segments(chemin);
    let meilleur = null;
    for (const definition of DEFINITIONS_MODULES) {
        for (const entree of definition.navigation) {
            const motif = segments(entree);
            if (motif.length === 0 ? cible.length !== 0 : !motif.every((s, i) => cible[i] === s)) {
                continue;
            }
            if (meilleur === null || motif.length > meilleur.longueur) {
                meilleur = { module: definition.id, longueur: motif.length };
            }
        }
    }
    return meilleur?.module ?? null;
}
/** Le module d'un type de travail du worker, ou `null` s'il n'est rattaché à rien. */
export function moduleDuTravail(type) {
    return DEFINITIONS_MODULES.find((definition) => definition.travaux.includes(type))?.id ?? null;
}
/**
 * TOUS les modules qu'un travail exige, selon son type ET sa charge (relecture
 * ARTO-56, F5) : le module du type, plus ceux qu'appelle ce qu'il rend. Un
 * `document:render` (module `documents-express`, du cœur) qui rend un
 * certificat, ou qui applique le modèle personnalisé de la galerie, exige en
 * plus `documents-prestige`. L'exécuteur contrôle chacun avant et pendant
 * l'exécution. Vide : le travail n'est rattaché à aucun module.
 */
export function modulesRequis(type, charge) {
    const module = moduleDuTravail(type);
    if (module === null)
        return [];
    const requis = [module];
    // L'essai d'un lot exige le module de sa nature, comme son import.
    if (type === 'reprise:essayer' && typeof charge === 'object' && charge !== null) {
        const nature = charge.nature;
        const moduleNature = typeof nature === 'string' ? moduleDuTravail(`import:${nature}`) : null;
        if (moduleNature !== null)
            requis.push(moduleNature);
    }
    if (type === 'media:variants' &&
        typeof charge === 'object' &&
        charge !== null &&
        'moduleSource' in charge &&
        charge.moduleSource === 'mouvements')
        requis.push('mouvements');
    if (type === 'document:render' && typeof charge === 'object' && charge !== null) {
        const { genre, modelePersonnalise, preparation } = charge;
        if (genre === 'annexe-assurance')
            requis.push('valeurs-assurance');
        if (genre === 'contrat-location')
            requis.push('location-oeuvres');
        if (GENRES_DOCUMENT_CONTRATS.includes(String(genre)))
            requis.push('modeles-contrats');
        if (GENRES_DOCUMENT_MOUVEMENTS.includes(String(genre)))
            requis.push('mouvements');
        if (GENRES_DOCUMENT_RESERVE.includes(String(genre)))
            requis.push('reserve-inventaire');
        if (GENRES_DOCUMENT_PRESTIGE.includes(String(genre)) || modelePersonnalise === true) {
            requis.push('documents-prestige');
        }
        if ((genre === 'fiche' || genre === 'liste-exposition') &&
            typeof preparation === 'object' &&
            preparation !== null &&
            'provenance' in preparation &&
            preparation.provenance === true)
            requis.push('editions-provenance');
        if (GENRES_DOCUMENT_OFFRES.includes(String(genre)))
            requis.push('offres-relances');
        if (genre === 'releve')
            requis.push('comptes-artistes');
    }
    return [...new Set(requis)];
}
/**
 * Les genres de `document:render` qui relèvent de `documents-prestige` :
 * certificats numérotés et dossiers d'œuvres (page commerciale).
 */
export const GENRES_DOCUMENT_PRESTIGE = [
    'certificat',
    'dossier-oeuvres',
    'dossier-artiste',
];
/** Le module qui possède un bloc du site public, ou `null`. */
export function moduleDuBlocSite(bloc) {
    return DEFINITIONS_MODULES.find((definition) => definition.blocsSite.includes(bloc))?.id ?? null;
}
/** Un bloc du site est-il affichable avec ces modules ? Un bloc inconnu l'est. */
export function blocSiteActif(bloc, modules) {
    const module = moduleDuBlocSite(bloc);
    return module === null || modules.includes(module);
}
/**
 * Les scopes d'un jeton qui restent valables avec ces modules : ceux d'un
 * module inactif sont retirés. Un scope qu'aucun module ne revendique passe.
 */
export function scopesEffectifs(scopes, modules) {
    const actifs = new Set(modules);
    return scopes.filter((scope) => {
        const proprietaire = DEFINITIONS_MODULES.find((definition) => definition.scopes.includes(scope));
        return proprietaire === undefined || actifs.has(proprietaire.id);
    });
}
/**
 * Anciennes valeurs de module et ce qu'elles deviennent (migration
 * `0004-modules-actifs`). `null` : la valeur est au cœur, ou a disparu.
 */
export const MODULES_RENOMMES = {
    factures: 'facturation',
    avoirs: 'facturation',
    ventes: 'facturation',
    newsletter: 'newsletter-reseaux',
    offres: 'offres-relances',
    'bons-depot': 'mouvements',
    'diffusion-artsy-artnet': 'artsy-artnet',
    'site-standard': 'site',
    'deux-facteurs': null,
};
// ── Quotas ───────────────────────────────────────────────────────────────────
/** Quotas d'un palier. `null` vaut « illimité ». */
export const quotasSchema = z.object({
    stockageGo: z.int().min(0),
    utilisateurs: z.int().min(1).nullable(),
});
/** Les quotas de chaque palier (6.4). */
export const QUOTAS_PAR_PALIER = {
    essentiel: { stockageGo: 20, utilisateurs: 3 },
    galerie: { stockageGo: 100, utilisateurs: 10 },
    groupe: { stockageGo: 500, utilisateurs: null },
};
/** Les quotas applicables : ceux du palier, sauf réglage propre à la galerie. */
export function quotasDeLaGalerie(gallery) {
    return gallery.quotas ?? QUOTAS_PAR_PALIER[gallery.palier];
}
/** Un octet de plus tiendrait-il encore dans le quota de stockage ? */
export function quotaStockageAtteint(gallery, usage) {
    const { stockageGo } = quotasDeLaGalerie(gallery);
    return usage.stockageOctets >= stockageGo * 1024 ** 3;
}
/** Peut-on encore inviter un membre ? */
export function quotaUtilisateursAtteint(gallery, usage) {
    const { utilisateurs } = quotasDeLaGalerie(gallery);
    return utilisateurs !== null && usage.utilisateurs >= utilisateurs;
}
/** Les genres de `document:render` qui relèvent de `mouvements` : bons et constats. */
export const GENRES_DOCUMENT_MOUVEMENTS = ['bon-mouvement', 'constat-etat'];
/** Les genres de `document:render` qui relèvent de `offres-relances`. */
export const GENRES_DOCUMENT_OFFRES = ['offre'];
export const GENRES_DOCUMENT_CONTRATS = ['contrat'];
// Les étiquettes sont au cœur : celles des emplacements et des caisses sont
// gardées par la route qui les imprime, la seule à les produire.
export const GENRES_DOCUMENT_RESERVE = ['rapport-inventaire'];
//# sourceMappingURL=modules.js.map